import { type ChildCustomer } from '@einwert/valuation-api-client'
import { createContext, useState, useContext } from 'react'

import {
    readFromLocalStorage,
    LOCAL_STORAGE_KEYS,
    writeToLocalStorage,
    removeFromLocalStorage,
} from 'ClientPortal/src/services/localStorage'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

type SelectedCustomerContextType = {
    selectedCustomer?: ChildCustomer
    setSelectedCustomerObject: (customer: ChildCustomer, navigateToDashboard?: boolean) => void
    removeSelectedCustomer: () => void
}

export const getSelectedCustomer = () => {
    return readFromLocalStorage<ChildCustomer>(LOCAL_STORAGE_KEYS.SELECTED_CUSTOMER)
}

export function SelectedCustomerProvider({ children }: { children: React.ReactNode }) {
    const navigate = useNavigate()
    const location = useLocation()
    const queryClient = useQueryClient()

    const [selectedCustomer, setSelectedCustomerState] = useState<ChildCustomer | undefined>(() =>
        readFromLocalStorage<ChildCustomer>(LOCAL_STORAGE_KEYS.SELECTED_CUSTOMER)
    )
    const setSelectedCustomerObject = (customer: ChildCustomer, navigateToDashboard: boolean = true) => {
        queryClient.removeQueries()
        writeToLocalStorage(LOCAL_STORAGE_KEYS.SELECTED_CUSTOMER, customer)
        setSelectedCustomerState(customer)

        const uuidRegex = /.*\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b.*/
        if (navigateToDashboard && uuidRegex.test(location.pathname)) {
            void navigate('/dashboard')
        }
    }

    const removeSelectedCustomer = () => {
        removeFromLocalStorage(LOCAL_STORAGE_KEYS.SELECTED_CUSTOMER)
    }

    return (
        <SelectedCustomerContext.Provider value={{ selectedCustomer, setSelectedCustomerObject, removeSelectedCustomer }}>
            {children}
        </SelectedCustomerContext.Provider>
    )
}

export function useSelectedCustomer() {
    const context = useContext(SelectedCustomerContext)
    if (context === null) {
        throw new Error('SelectedCustomerContext must be used within a SelectedCustomerProvider')
    }
    return context
}

export const SelectedCustomerContext = createContext<SelectedCustomerContextType | null>(null)
